/**
 * Drag-and-drop / click-to-pick image uploader that POSTs to the
 * shared ``/api/uploads`` (or ``/api/uploads/logo`` when ``variant="logo"``)
 * endpoint and surfaces the resulting S3 URL via ``onUploaded(url)``.
 *
 * Used everywhere a tenant admin / merchant configures an image —
 * branding logo, hero tiles, shop covers, category icons, product
 * photos. Replaces every "Image URL" text input on the platform with
 * a real upload so we never depend on operator-managed CDNs.
 *
 * iter53 — wired through ``lib/permissions.js`` so the OS camera
 * permission dialog fires *in-context* on the first tap inside a
 * Capacitor WebView (Play Store / App Store compliance: never ask
 * for camera at onboarding when the user hasn't tapped an upload).
 *
 * Props:
 *   - value:     current image URL (preview thumbnail)
 *   - onUploaded(url): called with the resolved S3 URL on success
 *   - variant:   "default" | "logo" — logo path force-resizes to 1024×1024 PNG
 *   - label:     header text shown above the dropzone (optional)
 *   - aspect:    preview aspect ratio class (Tailwind, e.g. "aspect-square")
 *   - className: extra wrapper classes
 *   - testId:    base data-testid (button gets `${testId}-button`, etc.)
 */
import { useRef, useState } from "react";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { requestCameraPermission, hasPromptedForCamera, isNativeApp } from "../lib/permissions";
import { useLse } from "../lib/lse";
import { compressImage } from "../lib/imageCompress";

/**
 * Read intrinsic pixel dimensions of a client-side image ``File`` via
 * an object-URL blob. Resolves to ``{w, h}`` on success, ``null`` on
 * failure (best-effort probe, never throws to the caller).
 */
function _readImageDimensions(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const w = img.naturalWidth || img.width;
            const h = img.naturalHeight || img.height;
            URL.revokeObjectURL(url);
            resolve(w && h ? { w, h } : null);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
        };
        img.src = url;
    });
}

export default function ImageUploader({
    value,
    onUploaded,
    variant = "default",
    label,
    aspect = "aspect-square",
    className = "",
    testId = "image-upload",
    acceptPdf = false,
}) {
    // Location Service Engine (image_compression): large photos are shrunk on the device before upload. Never for
    // logos — the icon / splash pipeline uses the uploaded logo as-is.
    const compress = useLse().has("image_compression") && variant !== "logo";
    const inp = useRef(null);
    const [busy, setBusy] = useState(false);
    const [drag, setDrag] = useState(false);

    const endpoint = variant === "logo" ? "/uploads/logo" : "/uploads";
    const accept = acceptPdf
        ? "image/png,image/jpeg,image/webp,image/jpg,application/pdf"
        : "image/png,image/jpeg,image/webp,image/jpg";

    /**
     * In-context camera permission warm-up. We surface the OS camera
     * prompt ONLY when the user has actually tapped the uploader, and
     * only on first tap (cached via ``hasPromptedForCamera``). On the
     * web (PWA) we skip the warm-up because the system file-picker
     * handles its own permission UX. We deliberately fail-open: even
     * if the user denies camera we still open the picker so they can
     * pick from gallery / files.
     */
    const openPicker = async (e) => {
        // The uploader is frequently rendered inside a <label> (e.g.
        // the admin <Field> component). A click anywhere inside a
        // label fires a synthetic activation click on its descendant
        // form control — so the hidden file input below would open
        // the OS picker a SECOND time right after we open it
        // programmatically. preventDefault() on the wrapper click
        // suppresses the label's default activation behaviour.
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (busy) return;
        if (isNativeApp() && !hasPromptedForCamera()) {
            try { await requestCameraPermission(); } catch { /* fail-open */ }
        }
        inp.current?.click();
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (compress && /^image\//.test(file.type)) file = await compressImage(file);
        const isImg = /^image\//.test(file.type);
        const isPdf = acceptPdf && file.type === "application/pdf";
        if (!isImg && !isPdf) {
            toast.error(acceptPdf
                ? "Pick an image (PNG/JPG/WebP) or PDF"
                : "Please pick an image (PNG/JPG/WebP)");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("File too large (max 8 MB)");
            return;
        }
        // iter137 — Soft resolution warning for logo uploads. The
        // app-icon + splash pipeline uses the uploaded image AS-IS
        // (no auto-upscale); sources under 1024×1024 will look soft
        // on high-density mobile screens. We warn but never block —
        // the admin can still upload a smaller logo knowingly.
        if (isImg && variant === "logo") {
            try {
                const dims = await _readImageDimensions(file);
                if (dims && (dims.w < 1024 || dims.h < 1024)) {
                    toast.warning(
                        `Logo is ${dims.w}×${dims.h}. For crisp app icons + splash, ` +
                        `upload at least 1024×1024. Continuing with the smaller image.`,
                        { duration: 6000 },
                    );
                }
            } catch { /* dimension probe is best-effort */ }
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(endpoint, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // The backend returns ``{url, key, ...}`` — the public URL
            // is the canonical handle every consumer of this component
            // wants (model fields are URLs, not keys). Callers that
            // need the raw S3 key can read it from the second arg.
            onUploaded?.(data.url, data);
            toast.success("Uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) handleFile(f);
    };

    const clear = (e) => {
        e.stopPropagation();
        onUploaded?.("");
    };

    return (
        <div className={className}>
            {label && (
                <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
                    {label}
                </div>
            )}
            <div
                data-testid={testId}
                onClick={openPicker}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                className={[
                    "relative rounded-2xl border-2 border-dashed cursor-pointer transition overflow-hidden",
                    aspect,
                    drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/60 bg-muted/30",
                    busy ? "opacity-60 pointer-events-none" : "",
                ].join(" ")}
            >
                {value ? (
                    <>
                        <img
                            src={value}
                            alt={label || "Uploaded image"}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <button
                            type="button"
                            data-testid={`${testId}-clear`}
                            onClick={clear}
                            className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white rounded-full p-1"
                            title="Remove"
                        >
                            <X size={14} />
                        </button>
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-[11px] font-semibold text-white inline-flex items-center gap-1">
                            <Upload size={12} /> Replace
                        </div>
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3 text-muted-foreground">
                        {busy ? (
                            <>
                                <Loader2 size={20} className="animate-spin mb-1" />
                                <span className="text-xs font-semibold">Uploading…</span>
                            </>
                        ) : (
                            <>
                                <ImageIcon size={20} className="mb-1" />
                                <span className="text-xs font-semibold">Click or drop image</span>
                                <span className="text-[10px] mt-0.5">PNG · JPG · WebP · 8 MB max</span>
                            </>
                        )}
                    </div>
                )}
                <input
                    ref={inp}
                    type="file"
                    accept={accept}
                    data-testid={`${testId}-file-input`}
                    // ``onClick`` and ``onChange`` here intentionally
                    // stop event propagation. The wrapper div opens the
                    // native picker by calling ``inp.current.click()``;
                    // without ``stopPropagation`` that synthetic click
                    // bubbles back up to the same wrapper div's onClick
                    // → re-opens the picker — causing the "select image
                    // popup appears 2-3 times" bug users reported.
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        e.stopPropagation();
                        const f = e.target.files?.[0];
                        // Reset the input value so picking the SAME
                        // file twice in a row still fires onChange.
                        e.target.value = "";
                        handleFile(f);
                    }}
                    className="hidden"
                />
            </div>
        </div>
    );
}
